import type { MenuItem } from "primereact/menuitem";
import { TABLE_COLUMNS_CONFIG, type ColumnMeta } from "../constants/table.cnst";
import { useEffect, useMemo, useRef, useState } from "react";
import { BreadCrumb } from "primereact/breadcrumb";
import { Card } from "primereact/card";
import { Calendar } from "primereact/calendar";
import { IconCalendar, IconDownload, IconFileTypeCsv, IconFileTypeXls, IconReload, IconSearch } from "@tabler/icons-react";
import { Button } from "primereact/button";
import { HBL_PA_RESOURCES, RAILWAY_ZONES } from "../constants/data.cnst";
import { ddmmyyyyToMmddyyyy, formatDateInDDMMYYYY } from "../services/util.service";
import { useMutation, useQuery } from "@tanstack/react-query";
import { APIMapping } from "../constants/api-mapping.cnst";
import { fetchApi } from "../services/http.service";
import { Toast } from "primereact/toast";
import { useLocation, useNavigate } from "react-router-dom";
import { MultiSelect } from "primereact/multiselect";
import { Menu } from "primereact/menu";
import Table from "../components/table";
import { Dropdown, type DropdownChangeEvent } from "primereact/dropdown";
import LineChart from "../components/line-chart";

export default function UnifiedKPI() {

    const breadcrumb: MenuItem[] = [
        { label: 'Unified KPI', style: { pointerEvents: 'none' }, className: 'font-semibold text-gray-900' }
    ];
    const tableColumns: ColumnMeta[] = TABLE_COLUMNS_CONFIG.UNIFIED_KPI_SUMMARY;
    const location = useLocation();
    const navigate = useNavigate();


    const toast = useRef<Toast>(null);

    const [displayTableData, setDisplayTableData] = useState<any[]>([]);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [pageNum, setPageNum] = useState(1);
    const [filtersChanged, setFiltersChanged] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);

    const { today, pastMonth } = useMemo(() => { const t = new Date(); const p = new Date(); p.setMonth(t.getMonth() - 1); return { today: t, pastMonth: p }; }, []);
    const [isQueryParamsProcessed, setIsQueryParamsProcessed] = useState(false);


    const [filters, setFilters] = useState({
        project: [] as string[],
        zone: [] as any[],
        date: [pastMonth, today] as (Date | null)[]
    });

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        const zones = params.get("zone")?.split(",") ?? [];
        const projects = params.get("project")?.split(",") ?? [];
        const fromDate = params.get("fromDate");
        const toDate = params.get("toDate");

        if (zones.length > 0 || fromDate || toDate || projects.length > 0) {
            setFilters(prev => ({
                ...prev,
                zone: RAILWAY_ZONES.filter(zone =>
                    zones.includes(zone.zoneCode)
                ),
                project: projects,
                date: fromDate
                    ? [
                        new Date(ddmmyyyyToMmddyyyy(fromDate)),
                        toDate ? new Date(ddmmyyyyToMmddyyyy(toDate)) : null
                    ]
                    : prev.date
            }));
        } else {
            handleReset();
        }

        setIsQueryParamsProcessed(true);
    }, [location.search]);


    const handleReset = () => {
        const resetFilters = {
            project: [],
            zone: [],
            date: [pastMonth, today]
        };
        setFiltersChanged(false);
        setFirst(0);
        setPageNum(1);
        setRows(10);
        setFilters(resetFilters);
        updateQueryParams(resetFilters);

        // unifiedKPIRefetch();
        unifiedKPIChartResponseRefetch();
        undesirableEBChartResponseRefetch();
        modeDegradationChartResponseRefetch();
        refetch();

        // if (shouldFetchReports) {
        //     setTimeout(() => {
        //         refetch();
        //     }, 100)
        // }
    };

    const updateQueryParams = (customFilters?: typeof filters) => {
        const f = customFilters ?? filters;
        const params = new URLSearchParams();

        if (f.zone.length) {
            params.set("zone", f.zone.map((item: any) => item.zoneCode).join(","));
        }

        if (f.project.length) {
            params.set("project", f.project.join(","));
        }

        if (f.date?.[0]) {
            params.set("fromDate", formatDateInDDMMYYYY(f.date[0]));
        }

        if (f.date?.[1]) {
            params.set("toDate", formatDateInDDMMYYYY(f.date[1]));
        }

        navigate(
            params.toString()
                ? `${location.pathname}?${params.toString()}`
                : location.pathname,
            { replace: true }
        );
    };

    const unifiedKPIQueryParams = useMemo<
        Record<string, string | number | boolean>
    >(() => {
        const params: Record<string, string | number | boolean> = {
            page_num: pageNum,
            total_entry_per_page: rows
        };

        if (filters.zone.length) {
            params.zone = filters.zone.map((item: any) => item.zoneCode).join(",");
        }

        if (filters.project.length) {
            params.project = filters.project.join(",");
        }

        if (filters.date?.[0]) params.fromDate = formatDateInDDMMYYYY(filters.date[0]);
        params.toDate = formatDateInDDMMYYYY(filters?.date[1] ?? filters.date[0]);

        return params;
    }, [filters, pageNum, rows]);

    // const unifiedKPIStats = [
    //     { label: "No. of Trips", value: '-', color: "#53D28C", key: "noOfTrips" },
    //     { label: "Total Kms run on Kavach", value: '-', color: "#9B59B6", key: "totalKavachKm" },
    //     { label: "Availability (FS + OS)", value: '-', color: "#5954ea", key: "availability" },
    //     { label: "Unified KPI", value: '-', color: "#54eaea", key: "unifiedKpi" },
    // ]

    // const { data: unifiedKPIApiResponseData, isFetching: isUnifiedKPIDataLoading, refetch: unifiedKPIRefetch } = useQuery({
    //     queryKey: ["unified-kpi-summary", JSON.stringify(unifiedKPIQueryParams)],
    //     queryFn: () =>
    //         fetchApi({
    //             endpoint: APIMapping.GET_UNIFIED_KPI,
    //             queryParams: unifiedKPIQueryParams,
    //             resourceName: HBL_PA_RESOURCES.UNIFIED_KPI
    //         }),
    //     staleTime: 0,
    //     gcTime: 0,
    //     enabled: false
    // });

    const chartQueryParams = useMemo(() => {
        const params: Record<string, string | number | boolean> = {};

        if (filters.zone.length) {
            params.zone = filters.zone
                .map((item: any) => item.zoneCode)
                .join(",");
        }

        if (filters.project.length) {
            params.project = filters.project.join(",");
        }

        if (filters.date?.[0]) {
            params.fromDate = formatDateInDDMMYYYY(filters.date[0]);
        }

        params.toDate = formatDateInDDMMYYYY(
            filters.date?.[1] ?? filters.date[0]
        );

        return params;
    }, [filters]);



    const { data: modeDegradationChartResponseData, isFetching: isModeDegradationChartResponseLoading, refetch: modeDegradationChartResponseRefetch } = useQuery({
        queryKey: ["mode-degradation-chart", chartQueryParams],
        queryFn: () =>
            fetchApi({
                endpoint: APIMapping.GET_UNIFIED_KPI_LINE_CHART_DATA,
                queryParams: { ...chartQueryParams, metric: 'MODE_DEGRADATION' },
                resourceName: HBL_PA_RESOURCES.UNIFIED_KPI
            }),
        enabled: false
    });

    const { data: undesirableEBChartResponseData, isFetching: isUndesirableEBChartResponseLoading, refetch: undesirableEBChartResponseRefetch } = useQuery({
        queryKey: ["undesirable-eb-chart", chartQueryParams],
        queryFn: () =>
            fetchApi({
                endpoint: APIMapping.GET_UNIFIED_KPI_LINE_CHART_DATA,
                queryParams: { ...chartQueryParams, metric: 'UNDESIRABLE_EB' },
                resourceName: HBL_PA_RESOURCES.UNIFIED_KPI
            }),
        enabled: false
    });

    const { data: unifiedKPIChartResponseData, isFetching: isUnifiedKPIChartResponseLoading, refetch: unifiedKPIChartResponseRefetch } = useQuery({
        queryKey: ["unified-kpi-chart", chartQueryParams],
        queryFn: () =>
            fetchApi({
                endpoint: APIMapping.GET_UNIFIED_KPI_LINE_CHART_DATA,
                queryParams: { ...chartQueryParams, metric: 'UNIFIED_KPI' },
                resourceName: HBL_PA_RESOURCES.UNIFIED_KPI
            }),
        enabled: false
    });


    // const unifiedKPIStatistics = useMemo(() => {
    //     if (!unifiedKPIApiResponseData?.response) return unifiedKPIStats;

    //     return unifiedKPIStats.map(stat => ({
    //         ...stat,
    //         value: unifiedKPIApiResponseData?.response?.[stat.key] ?? '-',
    //     }));
    // }, [unifiedKPIApiResponseData]);


    const periodOptionsForUndesirableEBTrip = useMemo(() => [
        {
            label: "Day",
            value: "Day",
            disabled: undesirableEBChartResponseData?.response?.day?.length === 0
        },
        {
            label: "Week",
            value: "Week",
            disabled: undesirableEBChartResponseData?.response?.week?.length === 0
        },
        {
            label: "Month",
            value: "Month",
            disabled: undesirableEBChartResponseData?.response?.month?.length === 0
        }
    ], [undesirableEBChartResponseData]);

    const [selectedPeriodForUndesirableEBTrip, setSelectedPeriodForUndesirableEBTrip] = useState<string | null>(null);

    useEffect(() => {
        const validOption = periodOptionsForUndesirableEBTrip.find(opt => !opt.disabled);
        setSelectedPeriodForUndesirableEBTrip(validOption?.value ?? null);
    }, [undesirableEBChartResponseData]);

    const undesirableEBTripHeader = (
        <div className="flex justify-between items-center">
            <h3 className="m-0 font-semibold">No. of Undesirable EB per trip per 1000 km</h3>

            <Dropdown
                value={selectedPeriodForUndesirableEBTrip}
                onChange={(e: DropdownChangeEvent) => setSelectedPeriodForUndesirableEBTrip(e.value)}
                options={periodOptionsForUndesirableEBTrip}
                placeholder="Select Period"
                className="w-14rem"
                disabled={(undesirableEBChartResponseData?.response?.day?.length === 0 && undesirableEBChartResponseData?.response?.week?.length === 0 && undesirableEBChartResponseData?.response?.month?.length === 0) || isUndesirableEBChartResponseLoading}
            />

        </div>
    );

    const periodOptionsForModeDegradationTrip = useMemo(() => [
        {
            label: "Day",
            value: "Day",
            disabled: modeDegradationChartResponseData?.response?.day?.length === 0
        },
        {
            label: "Week",
            value: "Week",
            disabled: modeDegradationChartResponseData?.response?.week?.length === 0
        },
        {
            label: "Month",
            value: "Month",
            disabled: modeDegradationChartResponseData?.response?.month?.length === 0
        }
    ], [modeDegradationChartResponseData]);

    const [selectedPeriodForModeDegradationTrip, setSelectedPeriodForModeDegradationTrip] = useState<string | null>(null);

    useEffect(() => {
        const validOption = periodOptionsForModeDegradationTrip.find(opt => !opt.disabled);
        setSelectedPeriodForModeDegradationTrip(validOption?.value ?? null);
    }, [modeDegradationChartResponseData]);

    const modeDegradationTripHeader = (
        <div className="flex justify-between items-center">
            <h3 className="m-0 font-semibold">No. of Mode Degradation per trip per 100 km</h3>

            <Dropdown
                value={selectedPeriodForModeDegradationTrip}
                onChange={(e: DropdownChangeEvent) => setSelectedPeriodForModeDegradationTrip(e.value)}
                options={periodOptionsForModeDegradationTrip}
                placeholder="Select Period"
                className="w-14rem"
                disabled={(modeDegradationChartResponseData?.response?.day?.length === 0 && modeDegradationChartResponseData?.response?.week?.length === 0 && modeDegradationChartResponseData?.response?.month?.length === 0) || isModeDegradationChartResponseLoading}
            />

        </div>
    );

    const periodOptionsForUnifiedKPIPerProject = useMemo(() => [
        {
            label: "Day",
            value: "Day",
            disabled: unifiedKPIChartResponseData?.response?.day?.length === 0
        },
        {
            label: "Week",
            value: "Week",
            disabled: unifiedKPIChartResponseData?.response?.week?.length === 0
        },
        {
            label: "Month",
            value: "Month",
            disabled: unifiedKPIChartResponseData?.response?.month?.length === 0
        }
    ], [unifiedKPIChartResponseData]);

    const [selectedPeriodForUnifiedKPIPerProject, setSelectedPeriodForUnifiedKPIPerProject] = useState<string | null>(null);

    useEffect(() => {
        const validOption = periodOptionsForUnifiedKPIPerProject.find(opt => !opt.disabled);
        setSelectedPeriodForUnifiedKPIPerProject(validOption?.value ?? null);
    }, [unifiedKPIChartResponseData]);

    const unifiedKPIPerProjectHeader = (
        <div className="flex justify-between items-center">
            <div>
                <h3 className="m-0 font-semibold">Unified KPI per Project</h3>
                <p className="mt-2 text-sm text-600 !font-normal">
                    Unified KPI = Availability (FS+OS) * (1- Undesirable EB per Trip per 1000 km) * (1 - Mode Degradation per Trip per 100 km)
                </p>
            </div>
            <Dropdown
                value={selectedPeriodForUnifiedKPIPerProject}
                onChange={(e: DropdownChangeEvent) => setSelectedPeriodForUnifiedKPIPerProject(e.value)}
                options={periodOptionsForUnifiedKPIPerProject}
                placeholder="Select Period"
                className="w-14rem"
                disabled={(unifiedKPIChartResponseData?.response?.day?.length === 0 && unifiedKPIChartResponseData?.response?.week?.length === 0 && unifiedKPIChartResponseData?.response?.month?.length === 0) || isUnifiedKPIChartResponseLoading}
            />

        </div>
    );


    const getChartData = (data: any, categories: any[], selectedPeriod: any) => {
        const period = selectedPeriod?.toLowerCase() || 'day';
        const periodData = data?.[period] ?? [];

        return {
            labels: periodData.map((item: any) => item.timeBucket),
            dateRange: period === 'day' ? [] : periodData.map((item: any) => item?.dateRange),

            datasets: categories.map((cat) => ({
                label: cat.zoneCode,
                data: periodData.map(
                    (item: any) => item.zones.find(
                        (rowItem: any) => rowItem.zone === cat.zoneCode
                    )?.value ?? 0
                ),
                projects: periodData.flatMap(
                    (item: any) => item.zones.find(
                        (rowItem: any) => rowItem.zone === cat.zoneCode
                    )?.projects
                ),

                borderColor: cat.backgroundColor,
                backgroundColor: cat.backgroundColor,
                tension: 0.4,
                pointRadius: 4,
                pointBackgroundColor: "#fff",
                pointBorderColor: cat.backgroundColor,
                fill: false
            }))
        };
    };


    const modeDegradationTripData = useMemo(
        () =>
            getChartData(
                modeDegradationChartResponseData?.response,
                RAILWAY_ZONES,
                selectedPeriodForModeDegradationTrip
            ),
        [modeDegradationChartResponseData, selectedPeriodForModeDegradationTrip]
    );

    const undesirableEBTripData = useMemo(
        () =>
            getChartData(
                undesirableEBChartResponseData?.response,
                RAILWAY_ZONES,
                selectedPeriodForUndesirableEBTrip
            ),
        [undesirableEBChartResponseData, selectedPeriodForUndesirableEBTrip]
    );

    const unifiedKPIPerprojectData = useMemo(
        () =>
            getChartData(
                unifiedKPIChartResponseData?.response,
                RAILWAY_ZONES,
                selectedPeriodForUnifiedKPIPerProject
            ),
        [unifiedKPIChartResponseData, selectedPeriodForUnifiedKPIPerProject]
    );

    const filtersRef = useRef(filters);

    const { data: unifiedKPISummaryApiResponseData, isFetching: isUnifiedKPISummaryDataLoading, refetch } = useQuery({
        queryKey: ["unified-kpi-summary-details", JSON.stringify(unifiedKPIQueryParams)],
        queryFn: () =>
            fetchApi({
                endpoint: APIMapping.GET_UNIFIED_KPI_SUMMARY,
                queryParams: unifiedKPIQueryParams,
                resourceName: HBL_PA_RESOURCES.UNIFIED_KPI
            }),
        staleTime: 0,
        gcTime: 0,
        enabled: false
    });


    useEffect(() => {
        if (!isQueryParamsProcessed) return;

        // unifiedKPIRefetch();
        unifiedKPIChartResponseRefetch();
        undesirableEBChartResponseRefetch();
        modeDegradationChartResponseRefetch();
    }, [isQueryParamsProcessed, location.pathname]);

    useEffect(() => {
        if (!isQueryParamsProcessed) return;
        refetch();
    }, [pageNum, rows, isQueryParamsProcessed, location.pathname]);



    useEffect(() => {
        setDisplayTableData(unifiedKPISummaryApiResponseData?.response?.data ?? []);
        setTotalRecords(unifiedKPISummaryApiResponseData?.response?.total_entries ?? 0);
    }, [unifiedKPISummaryApiResponseData, filtersChanged]);

    useEffect(() => {
        filtersRef.current = filters;
        setFiltersChanged(true);

        setPageNum(1);
        setRows(10);
        setFirst(0);
    }, [filters]);


    const exportQueryParams = useMemo(() => {
        if (!unifiedKPIQueryParams) return undefined;

        const { page_num, total_entry_per_page, ...rest } = unifiedKPIQueryParams;

        return rest;
    }, [unifiedKPIQueryParams]);


    const { mutateAsync: downloadReport, isPending: isDownloading } = useMutation({
        mutationFn: (format: "CSV" | "EXCEL") =>
            fetchApi({
                endpoint: APIMapping.REPORTS_EXPORT,
                responseType: "blob",
                queryParams: {
                    ...exportQueryParams,
                    format,
                    _ts: Date.now(),
                    reportType: 'UNIFIED_KPI'
                },
                resourceName: HBL_PA_RESOURCES.UNIFIED_KPI
            })
    });

    const handleDownload = async (format: "CSV" | "EXCEL") => {
        try {
            const result = await downloadReport(format);

            const blob = result.data as Blob;

            const contentDisposition = result.headers.get("content-disposition");
            let fileName = "Unified KPI";

            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match?.[1]) {
                    fileName = match[1];
                }
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");

            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();

            a.remove();
            window.URL.revokeObjectURL(url);

            toast.current?.show({
                severity: "success",
                summary: "Success",
                detail: `Unified KPI report downloaded successfully`,
                life: 3000
            });
        } catch (err) {
            console.error("Download failed", err);

            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Unable to download report. Please try again.",
                life: 4000
            });
        }
    };

    const items = [
        {
            label: "CSV",
            icon: <IconFileTypeCsv />,
            command: () => {
                handleDownload("CSV");
            }
        },
        {
            label: "Excel",
            icon: <IconFileTypeXls />,
            command: () => {
                handleDownload("EXCEL");
            }
        }
    ];
    const menu = useRef<Menu>(null);

    const header = (

        <>
            <div className="flex justify-between items-center">
                <h3 className="m-0 font-semibold">Unified KPI Summary</h3>

                <Button
                    label="Download"
                    icon={<IconDownload size={20} />}
                    loading={isDownloading}
                    disabled={isDownloading || displayTableData.length === 0}
                    className="bg-brand-600 border-brand-600 text-white dark:bg-brand-400 dark:border-brand-500 "
                    onClick={(event) => menu.current?.toggle(event)}
                />
                <Menu
                    popup
                    ref={menu}
                    model={items}
                    id="popup_menu"
                    popupAlignment="right"
                />
            </div>
        </>
    );

    const projectOptions = filters.zone?.length
        ? RAILWAY_ZONES
            .filter((zone: any) =>
                filters.zone.some(
                    (selectedZone: any) =>
                        selectedZone.zoneCode === zone.zoneCode
                )
            )
            .flatMap((zone: any) => zone.project ?? [])
        : RAILWAY_ZONES.flatMap((zone: any) => zone.project ?? []);



    return (
        <div className="h-[calc(100vh-4rem)] p-6 flex-1 overflow-y-auto">
            <Toast ref={toast} position="top-right" />

            <div className="flex items-center justify-between w-full my-3">
                <BreadCrumb model={breadcrumb} />
            </div>

            <div className="grid grid-cols-1 mb-6 pt-4">

                <Card header="Unified KPI">
                    {/* Header */}
                    {/* <div className="mb-3">
                            <h3 className="m-0 font-semibold"></h3>
                            <p className="mt-2 text-sm text-600">
                                {description}
                            </p>
                        </div> */}

                    {/* Filters */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">

                        <div>
                            <label className="block mb-2 text-sm font-medium">Zone</label>
                            <MultiSelect
                                value={filters.zone}
                                options={RAILWAY_ZONES}
                                optionLabel="zoneCode"
                                onChange={(e) => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        zone: e.target.value,
                                        project: []
                                    }))
                                }
                                }
                                filter
                                resetFilterOnHide
                                placeholder={`Select Zone`}
                                className="w-full"
                                display="chip"
                            />
                        </div>

                        <div>
                            <label className="block mb-2 text-sm font-medium">Project / Section</label>
                            <MultiSelect
                                options={projectOptions} // [{ label, value }]
                                value={filters.project}
                                onChange={(e) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        project: e.target.value
                                    }))
                                }
                                filter
                                resetFilterOnHide
                                placeholder="Select Section"
                                className="w-full"
                                display="chip"
                            />
                            {/* <InputText
                                    value={filters.tripId}
                                    onChange={(e) =>
                                        setFilters((prev) => ({
                                            ...prev,
                                            tripId: e.target.value
                                        }))
                                    }
                                    keyfilter={/^[0-9]*$/}
                                    placeholder="Enter Trip ID"
                                    className="w-full"
                                /> */}
                        </div>

                        {/* Date */}
                        <div className="flex flex-col items-start ">
                            <label className="block mb-2 text-sm font-medium">Date</label>
                            <div className="relative w-full">
                                <Calendar
                                    id="date"
                                    dateFormat="dd/mm/yy"
                                    value={filters.date}
                                    onChange={(e) =>
                                        setFilters({ ...filters, date: e.value as (Date | null)[] })
                                    }
                                    selectionMode="range"
                                    readOnlyInput
                                    hideOnRangeSelection
                                    numberOfMonths={2}
                                    placeholder="Select date"
                                    className="w-full min-w-64"
                                    inputClassName="w-64 pr-10"
                                />
                                <IconCalendar size={18} stroke={1.5} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none z-10" />
                            </div>
                        </div>

                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 mt-4">
                        <Button
                            label="Search"
                            icon={<IconSearch size={20} />}
                            onClick={() => {
                                setFiltersChanged(false);
                                setFirst(0);
                                setPageNum(1);
                                setRows(10);
                                updateQueryParams();

                                // unifiedKPIRefetch();
                                unifiedKPIChartResponseRefetch();
                                undesirableEBChartResponseRefetch();
                                modeDegradationChartResponseRefetch();
                                refetch();

                            }}

                            loading={isModeDegradationChartResponseLoading || isUnifiedKPISummaryDataLoading}
                            disabled={isModeDegradationChartResponseLoading || isUnifiedKPISummaryDataLoading}
                            className="bg-brand-600 border-brand-600 text-white dark:bg-brand-400 dark:border-brand-500 "
                        />
                        <Button
                            label="Reset"
                            icon={<IconReload size={20} />}
                            outlined
                            loading={isModeDegradationChartResponseLoading || isUnifiedKPISummaryDataLoading}
                            disabled={isModeDegradationChartResponseLoading || isUnifiedKPISummaryDataLoading}
                            className="bg-gray-200 border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-800 dark:text-gray-200"
                            onClick={handleReset}
                        />
                    </div>
                </Card>
            </div>


            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">

                {/* {unifiedKPIStatistics.map((event, index) => (
                    <div className="md:col-span-3" key={event.label}>
                        <Card className="h-full" key={index}>
                            <div className={`pl-5 relative ${index === 0 ? "trip-summary-tooltip cursor-pointer" : ""
                                }`}>
                                <span
                                    className="absolute left-0 top-0 w-[.25rem] rounded-full h-full"
                                    style={{ backgroundColor: event.color }}
                                ></span>
                                {isUnifiedKPIDataLoading ? (
                                    <>
                                        <Skeleton width="60%" height="1.5rem" className="mb-2" />
                                        <Skeleton width="40%" height="1.5rem" />
                                    </>
                                ) : (
                                    <>
                                        <p className="text-gray-700 dark:text-gray-100">{event.label}</p>
                                        <h2 className="text-3xl text-gray-900 dark:text-gray-100 font-bold">
                                            {event.value}
                                        </h2>
                                    </>
                                )}
                            </div>
                        </Card>
                    </div>
                ))} */}


                <div className="md:col-span-12">
                    <Card className="h-full" header={unifiedKPIPerProjectHeader}>

                        <LineChart id="unified-kpi-per-project" data={unifiedKPIPerprojectData} xOffset={true} isScroll yText="Unified KPI" />
                    </Card>
                </div>


                <div className="md:col-span-6">
                    <Card className="h-full" header={modeDegradationTripHeader}>

                        <LineChart id="mode-degradation-trip" data={modeDegradationTripData} xOffset={true} isScroll yText="Mode Deg. / Trip / 100km" />
                    </Card>
                </div>

                <div className="md:col-span-6">
                    <Card className="h-full" header={undesirableEBTripHeader}>

                        <LineChart id="undesirable-eb-trip" data={undesirableEBTripData} xOffset={true} isScroll yText="Undesirable EB / Trip / 1000km" />
                    </Card>
                </div>
            </div>


            <div className="grid grid-cols-1 mb-6 pt-4">
                <Card className="p-card-body-p-0" header={header}>
                    <Table
                        columns={tableColumns}
                        data={displayTableData}
                        showGridlines={true}
                        paginator={true}
                        isLoading={isUnifiedKPISummaryDataLoading}
                        first={first}
                        rows={rows}
                        totalRecords={totalRecords}

                        onPageChangeExternal={(page_num: any, total_entry_per_page: any) => {
                            setFirst((page_num - 1) * total_entry_per_page);
                            setRows(total_entry_per_page);
                            setPageNum(page_num);
                        }}
                    />
                </Card>
            </div>
        </div>
    );
}